import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffMasterApi, GENDERS, WORKER_TYPES, COLLARS, ROLL_STATUSES, PAY_TYPES } from '@/services/staffMasterApi';
import { errMsg } from '@/services/staffAttendanceApi';
import { StaffHistory } from '@/components/staffMaster/StaffHistory';

const EMPTY = { staff_id: '', staff_name: '', designation: '', section: '', dob: '', doj: '', gender: '', worker_type: '', collar: '', agency: '', pay_type: '', fixed_pay: '', roll_status: 'in_roll', left_date: '', phone: '', essl_code: '' };
const COLLAR_CHOICES = [...COLLARS, { value: 'none', label: 'Not set' }];
const PAY_CHOICES = [...PAY_TYPES, { value: 'none', label: 'Not set' }];
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const Choice = ({ options, value, onChange, testId }) => (
  <div className="flex gap-2" data-testid={testId}>
    {options.map((o) => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
        className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${value === o.value ? 'bg-[#2D5F4F] text-white border-[#2D5F4F]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#2D5F4F]'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

const Field = ({ label, children, required }) => (
  <div className="space-y-1">
    <Label className="text-xs text-gray-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

export const StaffFormDialog = ({ open, staff, sections, agencies = [], onClose, onSaved }) => {
  const isEdit = !!staff;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(staff ? { ...EMPTY, ...Object.fromEntries(Object.entries(staff).map(([k, v]) => [k, v ?? ''])) } : EMPTY);
  }, [open, staff]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const text = (key, props = {}) => (
    <Input value={form[key]} onChange={(e) => set({ [key]: e.target.value })} className="bg-white" data-testid={`staff-form-${key.replace('_', '-')}`} {...props} />
  );

  const save = async () => {
    if (!form.staff_id.trim() || !form.staff_name.trim()) { toast.error('Staff ID and Staff Name are required'); return; }
    if (!form.gender || !form.worker_type) { toast.error('Pick Gender and Worker Type'); return; }
    const contract = form.worker_type === 'contract';
    const fixed = !contract && form.pay_type === 'fixed';
    if (fixed && !(Number(form.fixed_pay) > 0)) { toast.error('Enter the Fixed Pay Amount (per month)'); return; }
    setBusy(true);
    const { staff_id, ...rest } = form;
    const body = {
      ...rest, agency: contract ? form.agency.trim() : '', left_date: form.roll_status === 'left' ? (form.left_date || today()) : null,
      pay_type: contract ? '' : form.pay_type, fixed_pay: fixed ? Number(form.fixed_pay) : null,
    };
    try {
      if (isEdit) await staffMasterApi.update(staff.staff_id, body);
      else await staffMasterApi.create({ staff_id: staff_id.trim(), ...body });
      toast.success(isEdit ? 'Staff updated' : 'Staff created');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save staff')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="staff-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${staff.staff_name}` : 'Add Staff'}</DialogTitle>
          <DialogDescription>Left staff can't scan their Staff ID or punch attendance; their history is kept.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-2">
          <Field label="Staff ID" required>{text('staff_id', { disabled: isEdit, placeholder: 'e.g. V0104' })}</Field>
          <Field label="Staff Name" required>{text('staff_name')}</Field>
          <Field label="Designation">{text('designation')}</Field>
          <Field label="Section">
            {text('section', { list: 'staff-section-options', placeholder: 'Pick or type' })}
            <datalist id="staff-section-options">{sections.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
          <Field label="Date of Birth">{text('dob', { placeholder: 'YYYY-MM-DD' })}</Field>
          <Field label="Date of Joining">{text('doj', { placeholder: 'YYYY-MM-DD' })}</Field>
          <Field label="Phone">{text('phone', { inputMode: 'tel' })}</Field>
          <Field label="eSSL Code (machine Employee Code)">
            <Input value={form.essl_code} onChange={(e) => set({ essl_code: e.target.value })} placeholder="e.g. 104" className="bg-white" data-testid="staff-form-essl-code" />
          </Field>
          <Field label="Gender" required><Choice options={GENDERS} value={form.gender} onChange={(v) => set({ gender: v })} testId="staff-form-gender" /></Field>
          <Field label="Worker Type" required><Choice options={WORKER_TYPES} value={form.worker_type} onChange={(v) => set({ worker_type: v })} testId="staff-form-worker-type" /></Field>
          <Field label="Collar"><Choice options={COLLAR_CHOICES} value={form.collar || 'none'} onChange={(v) => set({ collar: v === 'none' ? '' : v })} testId="staff-form-collar" /></Field>
          {form.worker_type === 'contract' && (
            <Field label="Agency (contract labour)">
              {text('agency', { list: 'staff-agency-options', placeholder: 'Pick or type agency' })}
              <datalist id="staff-agency-options">{agencies.map((a) => <option key={a} value={a} />)}</datalist>
            </Field>
          )}
          {form.worker_type !== 'contract' && (
            <Field label="Pay Type"><Choice options={PAY_CHOICES} value={form.pay_type || 'none'} onChange={(v) => set({ pay_type: v === 'none' ? '' : v })} testId="staff-form-pay-type" /></Field>
          )}
          {form.worker_type !== 'contract' && form.pay_type === 'fixed' && (
            <Field label="Fixed Pay Amount (₹ per month)" required>
              <Input type="number" min={0} step="0.01" value={form.fixed_pay} onChange={(e) => set({ fixed_pay: e.target.value })} placeholder="e.g. 15600" className="bg-white" data-testid="staff-form-fixed-pay" />
              {Number(form.fixed_pay) > 0 && <p className="text-[11px] text-gray-500" data-testid="staff-form-per-day">= {inr(Number(form.fixed_pay) / 26)} per present day (÷ 26). Piece-rate / supervisor pay is not paid.</p>}
            </Field>
          )}
          <Field label="In Roll / Left"><Choice options={ROLL_STATUSES} value={form.roll_status} onChange={(v) => set({ roll_status: v, left_date: v === 'left' ? (form.left_date || today()) : '' })} testId="staff-form-roll" /></Field>
          {form.roll_status === 'left' && <Field label="Left Date" required>{text('left_date', { type: 'date' })}</Field>}
        </div>
        {isEdit && <StaffHistory staffId={staff.staff_id} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="staff-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="staff-form-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{isEdit ? 'Save changes' : 'Create staff'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StaffFormDialog;
